import { useId, useRef, useState, type ComponentPropsWithoutRef, type Ref, type RefObject } from 'react'
import type { FieldErrors } from 'react-hook-form'

import { FieldShell } from '@atoms/form/FieldShell'
import * as SelectDropdown from '@atoms/form/SelectDropdown'
import { Combobox, ComboboxContent, ComboboxInput, ComboboxItem, ComboboxList, ComboboxTrigger } from '@atoms/Combobox'
import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'
import { useFormField } from '@hooks/useFormField'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('components.select')

const EMPTY_VALUE = '__empty__'

export type SelectFieldOption<T extends string> = { value: T; label: string; disabled?: boolean }

type ValueProps<T extends string> =
  | { emptyOptionLabel?: undefined; onValueChange: (value: T) => void }
  | { emptyOptionLabel: string; onValueChange: (value: T | null) => void }

export type SelectFieldProps<T extends string> = ValueProps<T> & {
  name: string
  label: string
  options: SelectFieldOption<T>[]
  value: T | null | undefined
  placeholder?: string
  description?: string
  errors?: FieldErrors
  required?: boolean
  disabled?: boolean
  hideLabel?: boolean
  search?: boolean
  className?: string
  ref?: Ref<HTMLButtonElement>
}

export function SelectField<T extends string>({
  name,
  label,
  options,
  value,
  placeholder = t('placeholder'),
  description,
  errors,
  required,
  disabled,
  hideLabel,
  search = false,
  className,
  ref,
  ...valueProps
}: SelectFieldProps<T>) {
  const id = useId()
  const labelId = `${id}-label`
  const triggerId = `${id}-trigger`
  const field = useFormField(name, errors, required, description)
  const shellRef = useRef<HTMLDivElement>(null)

  const choices: SelectFieldOption<string>[] =
    valueProps.emptyOptionLabel === undefined
      ? options
      : [{ value: EMPTY_VALUE, label: valueProps.emptyOptionLabel }, ...options]
  const currentValue = value ?? (valueProps.emptyOptionLabel === undefined ? '' : EMPTY_VALUE)

  const change = (next: string) =>
    (valueProps.onValueChange as (value: T | null) => void)(next === EMPTY_VALUE ? null : (next as T))

  const triggerProps = {
    ref,
    id: triggerId,
    'aria-labelledby': labelId,
    'aria-describedby': field.ariaDescribedBy,
    'aria-invalid': field.ariaInvalid || undefined,
    'aria-required': required || undefined,
  }

  return (
    <FieldShell
      ref={shellRef}
      field={field}
      label={label}
      labelId={labelId}
      hideLabel={hideLabel}
      description={description}
      className={className}
    >
      {search ? (
        <SearchableSelect
          name={name}
          choices={choices}
          value={currentValue}
          onChange={change}
          placeholder={placeholder}
          disabled={disabled}
          triggerProps={triggerProps}
          portalContainer={shellRef}
        />
      ) : (
        <SelectDropdown.Root value={currentValue} onValueChange={change} disabled={disabled}>
          <SelectDropdown.Trigger {...triggerProps}>
            <SelectDropdown.Value placeholder={placeholder} />
          </SelectDropdown.Trigger>
          <SelectDropdown.Content>
            {choices.map((option) => (
              <SelectDropdown.Item key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </SelectDropdown.Item>
            ))}
          </SelectDropdown.Content>
        </SelectDropdown.Root>
      )}
    </FieldShell>
  )
}

function SearchableSelect({
  name,
  choices,
  value,
  onChange,
  placeholder,
  disabled,
  triggerProps,
  portalContainer,
}: {
  name: string
  choices: SelectFieldOption<string>[]
  value: string
  onChange: (value: string) => void
  placeholder: string
  disabled?: boolean
  triggerProps: ComponentPropsWithoutRef<'button'> & { ref?: Ref<HTMLButtonElement> }
  portalContainer: RefObject<HTMLDivElement | null>
}) {
  const [query, setQuery] = useState('')
  const needle = query.toLowerCase()
  const startsWith = (option: SelectFieldOption<string>) => option.label.toLowerCase().startsWith(needle)
  const matches = choices
    .filter((option) => option.label.toLowerCase().includes(needle))
    .sort((a, b) => Number(startsWith(b)) - Number(startsWith(a)))
  const selectedLabel = choices.find((option) => option.value === value)?.label

  return (
    <Combobox
      name={name}
      value={value || null}
      onValueChange={(next: string | null) => next && onChange(next)}
      onInputValueChange={setQuery}
      filter={null}
      autoHighlight
      disabled={disabled}
    >
      <ComboboxTrigger
        {...triggerProps}
        className={SelectDropdown.triggerClassName}
        data-placeholder={selectedLabel ? undefined : ''}
      >
        <span>{selectedLabel ?? placeholder}</span>
        <SelectDropdown.TriggerChevron />
      </ComboboxTrigger>
      <ComboboxContent portalContainer={portalContainer}>
        <ComboboxInput placeholder={t('search_label')} aria-label={t('search_label')} />
        <p
          role="status"
          className={cn(
            'tw:m-0',
            matches.length === 0 ? 'tw:py-6 tw:text-center tw:text-sm tw:text-gray-400' : 'tw:sr-only'
          )}
        >
          {matches.length === 0 ? t('no_matches') : query && t('match_count', { count: matches.length })}
        </p>
        <ComboboxList>
          {matches.map((option) => (
            <ComboboxItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className="tw:py-1.5 tw:pl-2 tw:pr-8"
            >
              {option.label}
              {option.value === value && (
                <Icon type="check" width={16} height={16} className="tw:absolute tw:right-2" aria-hidden />
              )}
            </ComboboxItem>
          ))}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
